import { useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";

import ClockLight from "~/hec/assets/icons/clock-light";
import Quiz from "~/hec/assets/icons/quiz";
import Star from "~/hec/assets/icons/star";
import type { TransformedModuleSummary } from "~/hooks/learning-center/transformers";
import { useFetchModulePageUrl } from "~/hooks/learning-center/use-fetch-lms";
import useLearningCenterMutation from "~/hooks/learning-center/use-learning-center-mutation";
import useLearningCenter from "~/hooks/use-learning-center";
import { cn } from "~/lib/utils";

import messages from "./messages";

/**
 * SFCC-11504 — port of hec-composable `learning-center-levels/moduleCard.jsx`.
 * One module tile inside a level carousel: image, "Module N:" + title, a row of
 * lesson "stars" + a quiz icon showing completion, estimated time, and an
 * `X/Y Complete` count. Clicking starts the level/module (first visit) and
 * navigates to the module's lesson page.
 *
 * Deltas vs composable:
 *  - `useNavigate` (RR7) replaces `useHistory().push`.
 *  - `useFetchModulePageUrl(moduleID)` is positional (composable: `{moduleId}`).
 *  - Workers `startModule(moduleID, levelID, data, url)` takes the page url and
 *    folds in `setActiveLesson` — so we DON'T call `setActiveLesson` separately.
 */

interface ModuleCardProps {
  moduleData: TransformedModuleSummary;
  levelID: string;
  levelStarted: boolean;
  goalID: string;
}

const ModuleCard = ({
  moduleData,
  levelID,
  levelStarted,
  goalID,
}: ModuleCardProps) => {
  const {
    moduleID,
    image,
    title,
    moduleNumber,
    allLessonsQty,
    completeLessonsQty,
    estimatedCompletionTime,
    complete,
    started,
  } = moduleData;

  const intl = useIntl();
  const [requestInProgress, setRequestInProgress] = useState(false);
  const { startModule, startLevel } = useLearningCenterMutation();
  const { learningCenterData } = useLearningCenter();
  const { modulePageUrl } = useFetchModulePageUrl(moduleID);
  const navigate = useNavigate();

  const handleClick = async () => {
    if (requestInProgress || !moduleID) return;

    // Record level/module starts on first visit. `startModule` (Workers) takes
    // the page url and persists `activeLessonUrl` itself — no separate
    // `setActiveLesson` call (composable did both).
    if (!levelStarted || !started) {
      setRequestInProgress(true);
      const newData = structuredClone(learningCenterData);
      if (!levelStarted) startLevel(levelID, goalID, newData);
      const response = await startModule(
        moduleID,
        levelID,
        newData,
        modulePageUrl ?? "",
      );
      setRequestInProgress(false);
      if (!response.success) {
        console.error(
          "[LearningCenterLevels] startModule failed:",
          response.errorMsg,
        );
        return;
      }
    }

    if (modulePageUrl) navigate(modulePageUrl);
  };

  return (
    <button
      type="button"
      className="flex h-full w-full cursor-pointer flex-col rounded-md bg-white p-3"
      onClick={handleClick}
      data-testid="lms-module-card"
    >
      {image ? (
        <img
          className="aspect-video mb-4 w-full rounded-md object-cover"
          src={image}
          alt={title ?? ""}
          loading="lazy"
        />
      ) : (
        <div
          className="aspect-video mb-4 w-full rounded-md bg-neutral-50"
          aria-hidden="true"
        />
      )}
      <p className="body-header-common text-base">
        {intl.formatMessage(messages.moduleLabel, { moduleNumber })}
      </p>
      <p className="body-sm mb-2 text-left" data-testid="lms-module-card-title">
        {title}
      </p>
      <div className="mb-8 flex items-center">
        {Array.from({ length: Math.max(allLessonsQty - 1, 0) }).map(
          (_, lessonIndex) => (
            <Star
              key={lessonIndex}
              className={cn(
                "mr-2 h-[1.3125rem] w-[1.3125rem] [&>path]:fill-primary",
                lessonIndex >= completeLessonsQty && "opacity-40",
              )}
            />
          ),
        )}
        <Quiz
          className={cn(
            "h-[1.3125rem] w-[1.3125rem] [&>path]:fill-primary",
            !complete && "opacity-40",
          )}
        />
      </div>
      <div className="mt-auto flex w-full items-center justify-between">
        <div className="flex items-center">
          <ClockLight className="-mt-px mr-2 h-5 w-5" />
          <span className="body-sm mt-1">{estimatedCompletionTime}</span>
        </div>
        <div className="body-sm mt-1">
          {intl.formatMessage(messages.completeCount, {
            complete: completeLessonsQty,
            total: allLessonsQty,
          })}
        </div>
      </div>
    </button>
  );
};

export default ModuleCard;
